/**
 * Toast Helper Functions
 *
 * Convenience functions for displaying toast notifications.
 * Supports two calling patterns:
 * 1. With explicit addToast function: toastSuccess(addToast, "Message", "Title")
 * 2. Using global fallback: toastSuccess("Message", "Title")
 *
 * @module toastHelpers
 */

/**
 * Resolves the addToast function from either the first argument or the global fallback
 * @param {Function|string} fnOrMessage - Either a function or the message string
 * @returns {Function|null} The addToast function or null if not available
 */
function resolveAddToast(fnOrMessage) {
    if (typeof fnOrMessage === 'function') return fnOrMessage;
    if (
        typeof window !== 'undefined' &&
        typeof window.__ps_add_toast === 'function'
    ) {
        return window.__ps_add_toast;
    }
    return null;
}

/**
 * Display a success toast notification
 *
 * @param {Function|string} addToastOrMessage - Either the addToast function or the message
 * @param {string} [messageOrTitle] - Either the message (if first param is function) or title
 * @param {string} [maybeTitle] - Title if first two params are function and message
 * @returns {string|null} Toast ID or null if failed
 */
export function toastSuccess(addToastOrMessage, messageOrTitle, maybeTitle) {
    const candidate = resolveAddToast(addToastOrMessage);
    let message;
    let title;

    if (typeof addToastOrMessage === 'function') {
        message = messageOrTitle;
        title = maybeTitle;
    } else {
        message = addToastOrMessage;
        title = messageOrTitle;
    }

    if (candidate) {
        return candidate({
            title: title || null,
            description: message,
            variant: 'success',
        });
    }

    console.warn('toastSuccess: no addToast available', { message, title });
    return null;
}

/**
 * Display an error toast notification
 *
 * @param {Function|string} addToastOrMessage - Either the addToast function or the message
 * @param {string} [messageOrTitle] - Either the message (if first param is function) or title
 * @param {string} [maybeTitle] - Title if first two params are function and message
 * @returns {string|null} Toast ID or null if failed
 */
export function toastError(addToastOrMessage, messageOrTitle, maybeTitle) {
    const candidate = resolveAddToast(addToastOrMessage);
    let message;
    let title;

    if (typeof addToastOrMessage === 'function') {
        message = messageOrTitle;
        title = maybeTitle;
    } else {
        message = addToastOrMessage;
        title = messageOrTitle;
    }

    if (candidate) {
        return candidate({
            title: title || null,
            description: message,
            variant: 'error',
        });
    }

    console.warn('toastError: no addToast available', { message, title });
    return null;
}

/**
 * Display an info toast notification
 *
 * @param {Function|string} addToastOrMessage - Either the addToast function or the message
 * @param {string} [messageOrTitle] - Either the message (if first param is function) or title
 * @param {string} [maybeTitle] - Title if first two params are function and message
 * @returns {string|null} Toast ID or null if failed
 */
export function toastInfo(addToastOrMessage, messageOrTitle, maybeTitle) {
    const candidate = resolveAddToast(addToastOrMessage);
    let message;
    let title;

    if (typeof addToastOrMessage === 'function') {
        message = messageOrTitle;
        title = maybeTitle;
    } else {
        message = addToastOrMessage;
        title = messageOrTitle;
    }

    if (candidate) {
        return candidate({
            title: title || null,
            description: message,
            variant: 'info',
        });
    }

    console.warn('toastInfo: no addToast available', { message, title });
    return null;
}

/**
 * Display a warning toast notification
 *
 * @param {Function|string} addToastOrMessage - Either the addToast function or the message
 * @param {string} [messageOrTitle] - Either the message (if first param is function) or title
 * @param {string} [maybeTitle] - Title if first two params are function and message
 * @returns {string|null} Toast ID or null if failed
 */
export function toastWarn(addToastOrMessage, messageOrTitle, maybeTitle) {
    const candidate = resolveAddToast(addToastOrMessage);
    let message;
    let title;

    if (typeof addToastOrMessage === 'function') {
        message = messageOrTitle;
        title = maybeTitle;
    } else {
        message = addToastOrMessage;
        title = messageOrTitle;
    }

    if (candidate) {
        return candidate({
            title: title || null,
            description: message,
            variant: 'warning',
        });
    }

    console.warn('toastWarn: no addToast available', { message, title });
    return null;
}
